import {FC, createContext, useContext, useRef, useState, useEffect} from 'react'
import {shallowEqual, useSelector} from 'react-redux'
import {RootState} from 'setup'
import featureFlagService from 'app/services/FeatureFlagService'
import {flagKey} from 'app/constant/flag-key'

export interface IFeatureContext {
  isCustomerPmMultiWcsUpdate: boolean
  setIsCustomerPmMultiWcsUpdate: (value: boolean) => void
  isDeleteCustomerEnabled: boolean
  setIsDeleteCustomerEnabled: (value: boolean) => void
  isOrderSubtypeEnabled: boolean
  setIsOrderSubtypeEnabled: (value: boolean) => void
  isEnableExportReportsV2: boolean
  setIsEnableExportReportsV2: (value: boolean) => void
}

const FeatureFlag = createContext<IFeatureContext>({
  isCustomerPmMultiWcsUpdate: false,
  setIsCustomerPmMultiWcsUpdate: () => {},
  isDeleteCustomerEnabled: false,
  setIsDeleteCustomerEnabled: () => {},
  isOrderSubtypeEnabled: false,
  setIsOrderSubtypeEnabled: () => {},
  isEnableExportReportsV2: false,
  setIsEnableExportReportsV2: () => {},
})

const useFeatureFlag = () => {
  return useContext(FeatureFlag)
}

const FlagProvider: FC<any> = ({children}) => {
  const [isCustomerPmMultiWcsUpdate, setIsCustomerPmMultiWcsUpdate] = useState(false)
  const [isDeleteCustomerEnabled, setIsDeleteCustomerEnabled] = useState(false)
  const [isOrderSubtypeEnabled, setIsOrderSubtypeEnabled] = useState(false)
  const [isEnableExportReportsV2, setIsEnableExportReportsV2] = useState(false)

  const value: IFeatureContext = {
    isCustomerPmMultiWcsUpdate,
    setIsCustomerPmMultiWcsUpdate,
    isDeleteCustomerEnabled,
    setIsDeleteCustomerEnabled,
    isOrderSubtypeEnabled,
    setIsOrderSubtypeEnabled,
    isEnableExportReportsV2,
    setIsEnableExportReportsV2,
  }

  return (
    <FeatureFlag.Provider value={value}>
      <FeatureFlagContext>{children}</FeatureFlagContext>
    </FeatureFlag.Provider>
  )
}

const FeatureFlagContext: FC<any> = ({children}) => {
  const {setIsCustomerPmMultiWcsUpdate, setIsDeleteCustomerEnabled, setIsOrderSubtypeEnabled , setIsEnableExportReportsV2} =
    useFeatureFlag()
  const didRequest = useRef(false)
  const isAuthorized = useSelector<RootState>(({auth}) => auth.user, shallowEqual)

  useEffect(() => {
    const checkFeatureFlag = async (flagKey: string, onSetFlag: (value: boolean) => void) => {
      const isFlagEnabled = await featureFlagService.evaluation(flagKey)
      onSetFlag(isFlagEnabled)
    }

    const checkUserLocked = async () => {
      try {
        await checkFeatureFlag(
          flagKey.customerPaymentUpdateSubMultiple,
          setIsCustomerPmMultiWcsUpdate
        )
        await checkFeatureFlag(flagKey.deleteCustomer, setIsDeleteCustomerEnabled)
        await checkFeatureFlag(flagKey.orderSubtype, setIsOrderSubtypeEnabled)
        await checkFeatureFlag(flagKey.exportReports, setIsEnableExportReportsV2)
        didRequest.current = true
      } catch (err) {
        console.error(err)
      }
    }
    if (!didRequest.current && isAuthorized) {
      checkUserLocked()
    }
    // eslint-disable-next-line
  }, [])

  return children
}
export {useFeatureFlag, FeatureFlagContext, FlagProvider as FeatureFlagProvider}
